import { useEffect, useRef, useState } from "react";
import { logAction } from "../../api/logApi";
import {
  postAttributes,
  updateVtuberArrow,
  updateVtuberVisiable,
} from "../../api/detailApi";
import {
  rejectAttribute,
  removeApprovedAttribute,
} from "../../api/commonApi";
import { call, register, remove } from "../../funcStore";
import { recordTagInterest } from "../../utils/userPreference";
import TagList from "./TagList";

const styles = {
  profileSection: {
    background: "#fff",
    padding: "20px",
    borderRadius: "8px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    display: "flex",
    gap: "16px",
  },
  spProfileSection: {
    background: "#fff",
    padding: "20px",
    borderRadius: "8px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    gap: "16px",
  },
  profileIcon: {
    width: "96px",
    height: "96px",
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0,
  },
  description:{
    overflowWrap: "anywhere",
  },
  adminToggles: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "4px",
    marginTop: "8px",
  },
  arrowToggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "0.85rem",
    color: "#4b3f8f",
    cursor: "pointer",
  },
};

function VTuberProfile({ vtuber }) {
  // state
  const STATE_BEFORE_INIT = "beforeInit";
  const STATE_DISPLAY = "display";

  const [state, setState] = useState(STATE_BEFORE_INIT);
  const [isArrow, setIsArrow] = useState(vtuber.is_arrow ?? false);
  const [isVisiable, setIsVisiable] = useState(vtuber.is_visiable ?? false);

  const isArrowRef = useRef(isArrow);
  isArrowRef.current = isArrow;

  const isVisiableRef = useRef(isVisiable);
  isVisiableRef.current = isVisiable;


  const funcKeyRef = useRef("vtuberProfile");
  const funcKey = funcKeyRef.current;

  // data
  const dataRef = useRef({ clickedTag: "" });

  async function handleArrowToggle(e) {
    const nextValue = e.target.checked;
    setIsArrow(nextValue);

    try {
      await updateVtuberArrow(vtuber.id, nextValue);
      vtuber.is_arrow = nextValue;
      logAction("vtuber_arrow_toggle", { vtuberId: vtuber.id, isArrow: nextValue });
    } catch (error) {
      console.error(error);
      setIsArrow(!nextValue);
    }
  }

  async function handleVisiableToggle(e) {
    const nextValue = e.target.checked;
    setIsVisiable(nextValue);

    try {
      await updateVtuberVisiable(vtuber.id, nextValue);
      vtuber.is_visiable = nextValue;
      logAction("vtuber_visiable_toggle", {
        vtuberId: vtuber.id,
        isVisiable: nextValue,
      });
    } catch (error) {
      console.error(error);
      setIsVisiable(!nextValue);
    }
  }

  // is_visiable が true でも is_arrow が false の間は、
  // タグとヒートマップを出さない
  function isContentHidden() {
    return isVisiableRef.current && !isArrowRef.current;
  }

  function getClickTag() {
    const tag = dataRef.current.clickedTag;
    return tag;
  }

  function setClickTag(tag) {
    logAction("tag_click", { tag });
    recordTagInterest(tag);
    dataRef.current.clickedTag = tag;
    call("app.navigateToSearch");
  }

  function proposeAttribute(tags) {
    const values = Array.isArray(tags) ? tags : [tags];

    logAction("propose_attribute", { vtuberId: vtuber.id, tags: values });

    values.forEach((tag) => {
      if (!vtuber.attributes.some((attribute) => attribute.value === tag)) {
        vtuber.attributes.push({ value: tag, approved: false });
      }
    });

    call(`${funcKey}.tagList.refresh`);

    const userId = localStorage.getItem("user");

    postAttributes(userId, vtuber.id, values).catch((error) => {
      console.error(error);
    });
  }

  function removeAttribute(tag, approved) {
    logAction("attribute_admin_remove", { vtuberId: vtuber.id, tag, approved });

    vtuber.attributes = vtuber.attributes.filter(
      (attribute) => attribute.value !== tag,
    );

    call(`${funcKey}.tagList.refresh`);

    const removeFromServer = approved
      ? removeApprovedAttribute(vtuber.id, tag)
      : rejectAttribute(vtuber.id, tag);

    removeFromServer.catch((error) => {
      console.error(error);
    });
  }

  function init() {
    register(`${funcKey}.getClickTag`, getClickTag);
    register(`${funcKey}.getIsContentHidden`, () => isContentHidden());
    register(`${funcKey}.getAttributes`, () => vtuber.attributes);
    register(`${funcKey}.tagClick`, setClickTag);
    register(`${funcKey}.proposeAttribute`, proposeAttribute);
    register(`${funcKey}.removeAttribute`, removeAttribute);

    setState(STATE_DISPLAY);
  }

  // effect
  useEffect(() => {
    if (state === STATE_BEFORE_INIT) {
      init();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    if (state === STATE_BEFORE_INIT) {
      return;
    }

    call("vtuberDetailPage.setContentHidden", isContentHidden());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, isArrow, isVisiable]);

  useEffect(() => {
    return () => {
      remove(`${funcKey}.getClickTag`);
      remove(`${funcKey}.getIsContentHidden`);
      remove(`${funcKey}.getAttributes`);
      remove(`${funcKey}.tagClick`);
      remove(`${funcKey}.proposeAttribute`);
      remove(`${funcKey}.removeAttribute`);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state === STATE_BEFORE_INIT) {
    return null;
  }

  return (
    <section style={call("PCSP.getState") == "pc" ? styles.profileSection : styles.spProfileSection}>
      <img style={styles.profileIcon} src={vtuber.icon_url} alt={vtuber.name} />
      <div>
        <h2>{vtuber.name}</h2>
        <p style = {styles.description}>{vtuber.description}</p>
        <p>登録者数: {vtuber.subscriber_count?.toLocaleString() ?? 0}人</p>
        <a
          href={vtuber.youtube_url}
          target="_blank"
          rel="noreferrer"
          onClick={() =>
            logAction("channel_link_click", { vtuberId: vtuber.id })
          }
        >
          YouTubeチャンネルを見る
        </a>

        {call("app.isAdmin") && (
          <div style={styles.adminToggles}>
            <label style={styles.arrowToggle}>
              <input
                type="checkbox"
                checked={isArrow}
                onChange={handleArrowToggle}
              />
              検索結果に表示する(is_arrow)
            </label>

            <label style={styles.arrowToggle}>
              <input
                type="checkbox"
                checked={isVisiable}
                onChange={handleVisiableToggle}
              />
              タグ抜きで公開する(is_visiable)
            </label>
          </div>
        )}

        {!isContentHidden() && <TagList parentKey={funcKey} />}
      </div>
    </section>
  );
}

export default VTuberProfile;
